/**
 * Pure helpers for detecting "it's my turn" across the viewer's games.
 *
 * Kept free of React / RN so they're trivially unit-testable. Used by the
 * in-app turn watcher (hooks/useTurnNotifier) to raise a banner when a game
 * flips to the viewer's turn — independent of push delivery (which is flaky on
 * Android FCM and suppressed while the viewer is live in some OTHER game).
 */
import type { GameResponse, GamePlayerResponse } from '@warcarp/api-types'

/**
 * Resolve the player whose turn it is.
 *
 * IMPORTANT: `game.currentPlayerIndex` is a `playerIndex` VALUE (the server sets
 * it from `nextPlayer.playerIndex`), NOT an index into the `players` array.
 * Those diverge the moment a player is removed and leaves a gap in playerIndex
 * (e.g. players with playerIndex 0,1,2,4,5,6 — no 3), so `players[currentPlayerIndex]`
 * returns the wrong player. Look it up by the field, matching the API's own
 * admin resolution (`players.find(p => p.playerIndex === currentPlayerIndex)`).
 *
 * `eliminatedSlots` (optional): if the resolved player is eliminated/resigned,
 * walk forward in playerIndex order to the next live player — so a tile reads
 * the next player's name instead of a just-resigned one during the brief window
 * before the turn advances. Pass the slot-id strings from `state.eliminated`.
 */
export function currentPlayer(
  game: GameResponse,
  eliminatedSlots: readonly string[] = [],
): GamePlayerResponse | null {
  const ordered = [...game.players].sort((a, b) => a.playerIndex - b.playerIndex)
  const startPos = ordered.findIndex((p) => p.playerIndex === game.currentPlayerIndex)
  if (startPos < 0) return null
  const n = ordered.length
  for (let i = 0; i < n; i++) {
    const p = ordered[(startPos + i) % n]
    if (p && !eliminatedSlots.includes(String(p.slot))) return p
  }
  return null
}

/** True iff the game is active and the current player is the viewer. */
export function isMyTurn(game: GameResponse, userId: string | undefined): boolean {
  if (!userId) return false
  if (game.status !== 'ACTIVE') return false
  return currentPlayer(game)?.userId === userId
}

/**
 * Given the set of game ids that were already the viewer's turn last tick and
 * the latest games list, return the games that have NEWLY become the viewer's
 * turn (a not-my-turn → my-turn transition). Games already in `seen` are not
 * re-reported, so a banner fires once per transition, not every poll.
 */
export function newlyMyTurnGames(
  seen: ReadonlySet<string>,
  games: readonly GameResponse[],
  userId: string | undefined,
): GameResponse[] {
  return games.filter((g) => isMyTurn(g, userId) && !seen.has(g.id))
}

/** The set of game ids that are currently the viewer's turn. */
export function myTurnIds(
  games: readonly GameResponse[],
  userId: string | undefined,
): Set<string> {
  return new Set(games.filter((g) => isMyTurn(g, userId)).map((g) => g.id))
}
